import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiCall } from './config';

// Admin-Inbox für die WhatsApp-Konversationen: links die Chats (pro Person),
// rechts der Verlauf mit Antwortfeld. Nachrichten kommen über den Bot-Webhook
// rein und werden pro Nummer/Mitarbeiter gespeichert.
function WhatsAppInbox() {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [fillins, setFillins] = useState([]);
  const [mode, setMode] = useState('inbox'); // 'inbox' | 'broadcast'
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [msgHits, setMsgHits] = useState({}); // Volltext-Treffer: conversation_id → Snippet
  const [context, setContext] = useState(null); // Mitarbeiter-Kontext zum gewählten Chat
  // Broadcast-Zustand
  const [bcGroup, setBcGroup] = useState('active');
  const [bcRecipients, setBcRecipients] = useState([]);
  const [bcText, setBcText] = useState('');
  const [bcSending, setBcSending] = useState(false);
  const [bcResult, setBcResult] = useState('');
  const [broadcasts, setBroadcasts] = useState([]);
  const threadEnd = useRef(null);

  const CATS = [
    { key: 'all', label: 'Alle' },
    { key: 'employee_active', label: 'Aktive Mitarbeiter' },
    { key: 'wheelchair', label: 'Rollstuhlanfragen' },
    { key: 'employee_other', label: 'Andere Mitarbeiter' },
    { key: 'other', label: 'Sonstige' },
  ];

  const GROUP_LABEL = { active: 'Aktive Mitarbeiter', other: 'Andere Mitarbeiter', all: 'Alle Mitarbeiter', docs_open: 'Unterlagen noch offen' };
  const NUDGE_TEXTS = {
    docs_open: 'Hallo! 👋 Für die Wiesn 2026 fehlt uns von dir noch der ausgefüllte Arbeitsvertrag samt Unterlagen (z.B. Ausweis-Foto). Bitte hol das kurz online nach – Anmelden mit Nachname + Geburtsdatum, dann „Mein Arbeitsvertrag". Danke dir! 🙏',
  };

  const loadEmployees = useCallback(async (group) => {
    try {
      const data = await apiCall(`/admin/wa/employees?group=${group}`);
      setBcRecipients((Array.isArray(data) ? data : []).map((e) => ({ ...e, checked: true })));
    } catch (e) { setError(e.message); }
  }, []);

  const loadBroadcasts = useCallback(async () => {
    try { const d = await apiCall('/admin/wa/broadcasts'); setBroadcasts(Array.isArray(d) ? d : []); }
    catch (e) { /* Zusatz */ }
  }, []);

  const sendBroadcast = async () => {
    const recipients = bcRecipients.filter((r) => r.checked && r.phone);
    if (!bcText.trim() || !recipients.length) return;
    setBcSending(true); setBcResult('');
    try {
      const res = await apiCall('/admin/wa/broadcast', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: bcText.trim(), recipients, groupLabel: GROUP_LABEL[bcGroup] }),
      });
      setBcResult(`An ${res.sent} Empfänger verschickt${res.sent ? ' (aktuell DRYRUN – es geht noch nichts nach außen)' : ''}.`);
      setBcText('');
      await loadBroadcasts();
      await loadConversations();
    } catch (e) { setBcResult('Fehler: ' + e.message); }
    finally { setBcSending(false); }
  };

  const loadNotifications = useCallback(async () => {
    try {
      const data = await apiCall('/admin/wa/notifications');
      setNotifications(Array.isArray(data) ? data : []);
    } catch (e) { /* still. Meldungen sind Zusatz */ }
  }, []);

  const loadFillins = useCallback(async () => {
    try {
      const data = await apiCall('/admin/wa/fillin');
      setFillins(Array.isArray(data) ? data : []);
    } catch (e) { /* Zusatz */ }
  }, []);

  const resolveNotification = async (id) => {
    try {
      await apiCall(`/admin/wa/notifications/${id}/resolve`, { method: 'POST' });
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (e) { setError(e.message); }
  };

  // Einspring-Anfrage an die vorgeschlagenen Kandidaten starten
  const requestFillin = async (n) => {
    const cands = (n.meta && n.meta.best) || [];
    if (!cands.length) return;
    try {
      await apiCall('/admin/wa/fillin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date: n.meta.date,
          reason: n.body ? n.body.slice(0, 60) : 'Ausfall',
          candidates: cands.map((c) => ({ userId: c.id, name: c.name, phone: c.phone })),
        }),
      });
      await loadFillins();
    } catch (e) { setError('Anfrage fehlgeschlagen: ' + e.message); }
  };

  const confirmFillin = async (reqId, userId) => {
    try {
      await apiCall(`/admin/wa/fillin/${reqId}/confirm`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ userId }),
      });
      await loadFillins();
    } catch (e) { setError('Bestätigen fehlgeschlagen: ' + e.message); }
  };

  const cancelFillin = async (reqId) => {
    try {
      await apiCall(`/admin/wa/fillin/${reqId}/cancel`, { method: 'POST' });
      await loadFillins();
    } catch (e) { setError(e.message); }
  };

  const loadConversations = useCallback(async () => {
    try {
      const data = await apiCall('/admin/wa/conversations');
      setConversations(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  const loadMessages = useCallback(async (convId) => {
    try {
      const data = await apiCall(`/admin/wa/conversations/${convId}/messages`);
      setMessages(Array.isArray(data) ? data : []);
      // Ungelesen-Zähler lokal zurücksetzen (Server hat als gelesen markiert)
      setConversations((prev) => prev.map((c) => (c.id === convId ? { ...c, unread: 0 } : c)));
    } catch (e) {
      setError(e.message);
    }
  }, []);

  // Initial + Polling alle 15s
  useEffect(() => {
    loadConversations();
    loadNotifications();
    loadFillins();
    const t = setInterval(() => { loadConversations(); loadNotifications(); loadFillins(); }, 15000);
    return () => clearInterval(t);
  }, [loadConversations, loadNotifications, loadFillins]);

  // Verlauf des gewählten Chats alle 10s aktualisieren
  useEffect(() => {
    if (!selected) return;
    loadMessages(selected.id);
    const t = setInterval(() => loadMessages(selected.id), 10000);
    return () => clearInterval(t);
  }, [selected, loadMessages]);

  // Nach unten springen NUR beim Öffnen eines Chats – und wenn während des
  // Lesens eine neue Nachricht kommt, nur falls man ohnehin schon unten steht.
  // Vorher zog jedes 10-Sekunden-Polling den Verlauf wieder ans Ende, sobald
  // man weiter oben etwas nachlesen wollte.
  const scrolledConvRef = useRef(null);
  const lastMsgIdRef = useRef(null);
  useEffect(() => {
    if (!selected) return;
    const box = threadEnd.current?.parentElement;
    const newestId = messages.length ? messages[messages.length - 1].id : null;
    const chatGewechselt = scrolledConvRef.current !== selected.id;
    const neueNachricht = lastMsgIdRef.current !== null && newestId !== lastMsgIdRef.current;
    const stehtUnten = box ? (box.scrollHeight - box.scrollTop - box.clientHeight) < 80 : true;
    if (chatGewechselt || (neueNachricht && stehtUnten)) {
      threadEnd.current?.scrollIntoView({ behavior: chatGewechselt ? 'auto' : 'smooth' });
      scrolledConvRef.current = selected.id;
    }
    lastMsgIdRef.current = newestId;
  }, [messages, selected]);

  // Mitarbeiter-Kontext (Vertrag/Verfügbarkeit/nächste Schicht) zum gewählten Chat
  useEffect(() => {
    setContext(null);
    if (!selected?.user_id) return;
    let alive = true;
    apiCall(`/admin/wa/context/${selected.user_id}`)
      .then((d) => { if (alive) setContext(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, [selected?.id, selected?.user_id]);

  // Volltextsuche über ALLE Nachrichten (debounced, ab 2 Zeichen)
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setMsgHits({}); return; }
    const t = setTimeout(async () => {
      try {
        const rows = await apiCall(`/admin/wa/search?q=${encodeURIComponent(q)}`);
        const map = {};
        (Array.isArray(rows) ? rows : []).forEach((r) => { map[r.conversation_id] = r.snippet; });
        setMsgHits(map);
      } catch { /* Suche ist Zusatzfunktion – Fehler still ignorieren */ }
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Broadcast-Modus: Empfänger der gewählten Gruppe + Verlauf laden
  useEffect(() => {
    if (mode === 'broadcast') { loadEmployees(bcGroup); loadBroadcasts(); }
  }, [mode, bcGroup, loadEmployees, loadBroadcasts]);

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setSending(true);
    setError('');
    try {
      const resp = await apiCall(`/admin/wa/conversations/${selected.id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: reply.trim() }),
      });
      // Server pausiert den Bot 30 Min in diesem Chat – lokal anzeigen
      if (resp && resp.bot_paused_until) {
        setSelected((s) => (s ? { ...s, bot_paused_until: resp.bot_paused_until } : s));
        setConversations((prev) => prev.map((c) => (c.id === selected.id ? { ...c, bot_paused_until: resp.bot_paused_until } : c)));
      }
      setReply('');
      await loadMessages(selected.id);
    } catch (e) {
      setError('Antwort konnte nicht gesendet werden: ' + e.message);
    } finally {
      setSending(false);
    }
  };

  const fmtTime = (s) => {
    if (!s) return '';
    const d = new Date(s.replace(' ', 'T') + 'Z');
    return d.toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
  };

  const title = (c) => c.display_name || c.wa_number;

  const catCount = (k) => (k === 'all' ? conversations.length : conversations.filter((c) => c.category === k).length);
  const catConvs = categoryFilter === 'all' ? conversations : conversations.filter((c) => c.category === categoryFilter);
  // Live-Suche über Name, Nummer, letzte Nachricht – UND Volltext-Treffer aus dem
  // gesamten Verlauf (msgHits kommt debounced vom Server).
  const q = search.trim().toLowerCase();
  const visibleConvs = !q ? catConvs : catConvs.filter((c) =>
    (c.display_name || '').toLowerCase().includes(q) ||
    (c.wa_number || '').toLowerCase().includes(q) ||
    (c.last_body || '').toLowerCase().includes(q) ||
    msgHits[c.id] !== undefined
  );

  // Läuft die Bot-Pause für diesen Chat noch?
  const pausedUntilDate = (c) => {
    if (!c || !c.bot_paused_until) return null;
    const d = new Date(String(c.bot_paused_until).replace(' ', 'T') + 'Z');
    return d > new Date() ? d : null;
  };
  // Bot-Pause setzen (Minuten) oder mit 0 sofort beenden
  const setBotPause = async (minutes) => {
    if (!selected) return;
    try {
      const r = await apiCall(`/admin/wa/conversations/${selected.id}/bot-pause`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ minutes }),
      });
      const val = r && r.bot_paused_until ? r.bot_paused_until : null;
      setSelected((s) => (s ? { ...s, bot_paused_until: val } : s));
      setConversations((prev) => prev.map((c) => (c.id === selected.id ? { ...c, bot_paused_until: val } : c)));
    } catch (_) { /* still, Anzeige bleibt wie sie ist */ }
  };

  const checkedCount = bcRecipients.filter((r) => r.checked).length;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <h4 className="mb-1"><i className="bi bi-whatsapp me-2 text-success"></i>WhatsApp-Nachrichten</h4>
          <small className="text-muted">Chats mitlesen &amp; beantworten, Rundnachrichten senden</small>
        </div>
        <div className="btn-group">
          <button className={`btn btn-sm ${mode === 'inbox' ? 'btn-success' : 'btn-outline-success'}`} onClick={() => setMode('inbox')}>
            <i className="bi bi-inbox me-1"></i>Posteingang
          </button>
          <button className={`btn btn-sm ${mode === 'broadcast' ? 'btn-success' : 'btn-outline-success'}`} onClick={() => setMode('broadcast')}>
            <i className="bi bi-megaphone me-1"></i>Broadcast
          </button>
        </div>
      </div>

      {error && <div className="alert alert-warning py-2">{error}</div>}

      {notifications.length > 0 && (
        <div className="mb-3">
          {notifications.map((n) => (
            <div key={n.id} className={`alert d-flex justify-content-between align-items-start py-2 mb-2 ${n.severity === 'high' ? 'alert-danger' : 'alert-info'}`}>
              <div style={{ whiteSpace: 'pre-wrap' }}>
                <strong>{{
                  sick: '🤒 Krankmeldung', operational: '🔴 Betriebsbedarf',
                  shift_change: '🔀 Schicht-Wunsch', declined: '🚫 Saison-Absage',
                  returning: '🔁 Nächstes Jahr dabei', report: '🗣️ Meldung (andere Person)',
                  referral: '👥 Empfehlung', number_change: '📱 Neue Nummer',
                  application: '🆕 Neue Bewerbung', question: '❓ Anliegen (weitergeleitet)',
                  luggage: '🧳 Gepäck-Versandanfrage',
                  frustration: '⚠️ Nutzer unzufrieden',
                }[n.category] || '🔔 Meldung'}</strong>
                <span className="text-muted small ms-2">{fmtTime(n.created_at)}</span>
                {/* Wer? Name (falls Mitarbeiter bekannt) + Nummer – sonst muss man
                    die Meldung erst mühsam einem Chat zuordnen. */}
                {(n.firstname || n.wa_number) && (
                  <div className="small fw-bold mt-1">
                    <i className="bi bi-person me-1"></i>
                    {n.firstname ? `${n.firstname} ${n.lastname || ''}`.trim() : 'Unbekannt'}
                    {n.wa_number && <span className="text-muted fw-normal ms-2">+{n.wa_number}</span>}
                  </div>
                )}
                <div className="small">{n.body}</div>
                {n.meta && n.meta.best && n.meta.best.length > 0 && (
                  <div className="small mt-1">
                    <em>Könnten einspringen:</em> {n.meta.best.map((c) => c.name).join(', ')}
                  </div>
                )}
              </div>
              <div className="ms-2 flex-shrink-0 text-end">
                {n.category === 'sick' && n.meta && n.meta.best && n.meta.best.length > 0 && (
                  <button className="btn btn-sm btn-primary d-block mb-1 w-100" onClick={() => requestFillin(n)}>
                    <i className="bi bi-send me-1"></i>Einspringer anfragen
                  </button>
                )}
                {n.conversation_id && (
                  <button className="btn btn-sm btn-outline-primary d-block mb-1 w-100"
                    title="Zum Chat dieser Person springen"
                    onClick={() => {
                      setMode('inbox');
                      const conv = conversations.find((c) => c.id === n.conversation_id)
                        || { id: n.conversation_id, wa_number: n.wa_number, user_id: n.user_id };
                      setSelected(conv);
                    }}>
                    <i className="bi bi-chat-dots me-1"></i>Chat öffnen
                  </button>
                )}
                <button className="btn btn-sm btn-outline-secondary" onClick={() => resolveNotification(n.id)}>
                  erledigt
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {mode === 'inbox' && (<>
      {fillins.length > 0 && (
        <div className="mb-3">
          {fillins.map((r) => (
            <div key={r.id} className="border rounded p-2 mb-2 bg-white">
              <div className="d-flex justify-content-between align-items-center">
                <strong><i className="bi bi-people me-1"></i>Einspringer gesucht – {fmtDate(r.date)}</strong>
                <button className="btn btn-sm btn-link text-muted p-0" onClick={() => cancelFillin(r.id)}>abbrechen</button>
              </div>
              {r.reason && <div className="small text-muted">{r.reason}</div>}
              <div className="mt-1 d-flex flex-wrap gap-2">
                {r.offers.map((o) => (
                  <span key={o.id} className="d-inline-flex align-items-center border rounded px-2 py-1 small">
                    {o.name}
                    <span className={`badge ms-2 ${o.status === 'accepted' ? 'bg-success' : o.status === 'declined' ? 'bg-secondary' : o.status === 'confirmed' ? 'bg-primary' : 'bg-warning text-dark'}`}>
                      {o.status === 'pending' ? 'gefragt' : o.status === 'accepted' ? 'sagt zu' : o.status === 'declined' ? 'abgesagt' : o.status === 'confirmed' ? 'eingeteilt' : o.status}
                    </span>
                    {o.status === 'accepted' && (
                      <button className="btn btn-sm btn-success ms-2 py-0" onClick={() => confirmFillin(r.id, o.user_id)}>
                        bestätigen
                      </button>
                    )}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="d-flex flex-wrap gap-2 mb-2">
        {CATS.map((cat) => (
          <button
            key={cat.key}
            onClick={() => setCategoryFilter(cat.key)}
            className={`btn btn-sm ${categoryFilter === cat.key ? 'btn-dark' : 'btn-outline-secondary'}`}
          >
            {cat.label} <span className="badge bg-light text-dark ms-1">{catCount(cat.key)}</span>
          </button>
        ))}
        <button className="btn btn-sm btn-link text-muted ms-auto" onClick={loadConversations}>
          <i className="bi bi-arrow-clockwise me-1"></i>Aktualisieren
        </button>
      </div>

      <div className="row g-0 border rounded overflow-hidden" style={{ height: '65vh' }}>
        {/* Liste */}
        <div className="col-md-4 border-end h-100 overflow-auto bg-light">
          <div className="p-2 border-bottom bg-white" style={{ position: 'sticky', top: 0, zIndex: 5 }}>
            <div className="position-relative">
              <i className="bi bi-search position-absolute text-muted" style={{ left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px' }}></i>
              <input
                type="search"
                className="form-control form-control-sm"
                style={{ paddingLeft: '30px' }}
                placeholder="Chats durchsuchen…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          {visibleConvs.length === 0 ? (
            <div className="p-3 text-muted small">{q ? <>Keine Treffer für „{search.trim()}".</> : 'Keine Chats in dieser Kategorie.'}</div>
          ) : visibleConvs.map((c) => (
            <button
              key={c.id}
              onClick={() => setSelected(c)}
              className={`w-100 text-start border-0 border-bottom p-3 d-flex justify-content-between align-items-start ${selected?.id === c.id ? 'bg-white' : 'bg-light'}`}
              style={{ cursor: 'pointer' }}
            >
              <div style={{ minWidth: 0 }}>
                <div className="fw-bold text-truncate">
                  {c.is_employee
                    ? <span><i className="bi bi-person-badge me-1 text-primary"></i>{title(c)}</span>
                    : <span><i className="bi bi-person me-1 text-secondary"></i>{title(c)}</span>}
                </div>
                {q && msgHits[c.id] !== undefined && !(c.last_body || '').toLowerCase().includes(q) ? (
                  <div className="small text-muted text-truncate fst-italic" style={{ maxWidth: '95%' }}>
                    🔎 {msgHits[c.id]}
                  </div>
                ) : (
                  <div className="small text-muted text-truncate" style={{ maxWidth: '95%' }}>
                    {c.last_dir === 'out' ? '↩ ' : ''}{c.last_body || ''}
                  </div>
                )}
              </div>
              <div className="text-end ms-2 flex-shrink-0">
                <div className="small text-muted">{fmtTime(c.last_inbound_at || c.last_outbound_at)}</div>
                {c.unread > 0 && <span className="badge bg-success rounded-pill mt-1">{c.unread}</span>}
              </div>
            </button>
          ))}
        </div>

        {/* Verlauf */}
        <div className="col-md-8 h-100 d-flex flex-column">
          {!selected ? (
            <div className="d-flex h-100 align-items-center justify-content-center text-muted">
              Wähle links einen Chat aus.
            </div>
          ) : (
            <>
              <div className="p-2 border-bottom bg-white">
                <div>
                  <strong>{title(selected)}</strong>
                  {selected.display_name && <span className="text-muted small ms-2">{selected.wa_number}</span>}
                  {selected.lang === 'foreign' && (
                    <span className="badge bg-info text-dark ms-2"
                      title="Diese Person bekommt die Bot-Antworten automatisch übersetzt – hier im Postfach siehst du die deutsche Fassung.">
                      🌍 {String(selected.lang_ref || '').replace(/^LANGUAGE=/, '') || 'übersetzt'}
                    </span>
                  )}
                  {!selected.is_employee && <span className="badge bg-secondary ms-2">Kunde</span>}
                  {pausedUntilDate(selected) ? (
                    <span className="badge bg-warning text-dark ms-2 d-inline-flex align-items-center gap-1"
                      title="Solange du hier antwortest, hält sich der Bot zurück. Antworten dieser Person werden dir gepusht.">
                      🤖 Bot pausiert bis {pausedUntilDate(selected).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                      <button type="button" className="btn btn-sm btn-success py-0 px-1 ms-1" style={{ lineHeight: 1.1 }}
                        title="Bot wieder übernehmen lassen" onClick={() => setBotPause(0)}>▶︎</button>
                    </span>
                  ) : (
                    <button type="button" className="btn btn-sm btn-outline-secondary ms-2 py-0 px-2"
                      title="Bot pausieren – ich übernehme diesen Chat (4 Stunden)" onClick={() => setBotPause(240)}>
                      ⏸ Bot pausieren
                    </button>
                  )}
                </div>
                {context && (
                  <div className="d-flex flex-wrap gap-2 mt-1 small">
                    <span className={`badge ${{
                      approved: 'bg-success', submitted: 'bg-info text-dark', pending: 'bg-info text-dark',
                      draft: 'bg-warning text-dark', rejected: 'bg-danger',
                    }[(context.contract_status || 'none').toLowerCase()] || 'bg-secondary'}`}>
                      📄 Vertrag: {{
                        approved: 'genehmigt', submitted: 'eingereicht', pending: 'in Prüfung',
                        draft: 'Entwurf', rejected: 'abgelehnt', none: 'fehlt',
                      }[(context.contract_status || 'none').toLowerCase()] || context.contract_status}
                    </span>
                    <span className={`badge ${context.availability_days > 0 ? 'bg-success' : 'bg-secondary'}`}>
                      📅 Verfügbarkeit: {context.availability_days > 0 ? `${context.availability_days} Tage` : 'keine'}
                    </span>
                    <span className="badge bg-light text-dark border">
                      ⏭️ {context.next_shift
                        ? `${fmtDate(context.next_shift.date)} ${context.next_shift.station_id} ${context.next_shift.start_time}–${context.next_shift.end_time}`
                        : 'keine Schicht geplant'}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex-grow-1 overflow-auto p-3" style={{ background: '#efe7dd' }}>
                {messages.map((m, i) => (
                  <div key={i} className={`d-flex mb-2 ${m.direction === 'in' ? 'justify-content-start' : 'justify-content-end'}`}>
                    <div
                      className="px-3 py-2 rounded shadow-sm"
                      style={{
                        maxWidth: '75%',
                        whiteSpace: 'pre-wrap',
                        background: m.direction === 'in' ? '#ffffff' : (m.intent === 'admin' ? '#d1e7ff' : '#dcf8c6'),
                      }}
                    >
                      <div>{m.body}</div>
                      <div className="text-muted text-end" style={{ fontSize: '0.7rem' }}>
                        {m.direction === 'out' && (m.intent === 'admin' ? '👤 ' : '🤖 ')}{fmtTime(m.created_at)}
                        {m.direction === 'out' && m.status && (
                          <span
                            className="ms-1"
                            style={{ color: m.status === 'read' ? '#0d6efd' : (m.status === 'failed' ? '#dc3545' : '#6c757d') }}
                          >
                            {' · '}{{ sent: '✓ Gesendet', delivered: '✓✓ Zugestellt', read: '✓✓ Gelesen', failed: '⚠️ Fehlgeschlagen' }[m.status] || ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={threadEnd} />
              </div>

              <div className="p-2 border-top bg-white">
                <div className="input-group">
                  <textarea
                    className="form-control"
                    rows="1"
                    placeholder="Antwort schreiben…"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                  />
                  <button className="btn btn-success" onClick={sendReply} disabled={sending || !reply.trim()}>
                    <i className="bi bi-send"></i>
                  </button>
                </div>
                <small className="text-muted">Enter = senden, Shift+Enter = neue Zeile</small>
              </div>
            </>
          )}
        </div>
      </div>
      </>)}

      {mode === 'broadcast' && (
        <div className="row g-3">
          {/* Composer */}
          <div className="col-md-7">
            <div className="border rounded p-3 bg-white">
              <h6><i className="bi bi-megaphone me-2"></i>Rundnachricht senden</h6>
              <div className="mb-2">
                <label className="form-label small mb-1">Empfängergruppe</label>
                <select className="form-select form-select-sm" value={bcGroup} onChange={(e) => setBcGroup(e.target.value)}>
                  <option value="active">Aktive Mitarbeiter</option>
                  <option value="docs_open">Unterlagen noch offen</option>
                  <option value="other">Andere Mitarbeiter</option>
                  <option value="all">Alle Mitarbeiter</option>
                </select>
              </div>
              <textarea
                className="form-control mb-1" rows="4"
                placeholder="Nachricht an die ausgewählten Empfänger…"
                value={bcText} onChange={(e) => setBcText(e.target.value)}
              />
              {NUDGE_TEXTS[bcGroup] && (
                <button className="btn btn-link btn-sm p-0 mb-2" onClick={() => setBcText(NUDGE_TEXTS[bcGroup])}>
                  <i className="bi bi-magic me-1"></i>Textvorschlag einfügen
                </button>
              )}
              <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">{checkedCount} von {bcRecipients.length} ausgewählt</small>
                <button className="btn btn-success btn-sm" onClick={sendBroadcast} disabled={bcSending || !bcText.trim() || !checkedCount}>
                  <i className="bi bi-send me-1"></i>An {checkedCount} senden
                </button>
              </div>
              {bcResult && <div className="alert alert-info py-2 mt-2 mb-0 small">{bcResult}</div>}
              <div className="alert alert-warning py-2 mt-2 mb-0 small">
                <i className="bi bi-info-circle me-1"></i>Aktuell <strong>DRYRUN</strong> – Nachrichten werden erfasst, aber noch nicht real verschickt (bis Meta angebunden ist).
              </div>
            </div>

            {broadcasts.length > 0 && (
              <div className="border rounded p-3 bg-white mt-3">
                <h6 className="mb-2"><i className="bi bi-clock-history me-2"></i>Gesendete Broadcasts</h6>
                {broadcasts.map((b) => (
                  <div key={b.id} className="border-bottom py-2 small">
                    <div className="text-muted">{fmtTime(b.created_at)} · {b.group_label || '—'} · {b.recipient_count} Empfänger</div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{b.body}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Empfängerliste */}
          <div className="col-md-5">
            <div className="border rounded bg-white" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              <div className="p-2 border-bottom d-flex justify-content-between align-items-center">
                <strong className="small">Empfänger ({bcRecipients.length})</strong>
                <div>
                  <button className="btn btn-link btn-sm p-0 me-2" onClick={() => setBcRecipients((p) => p.map((r) => ({ ...r, checked: true })))}>alle</button>
                  <button className="btn btn-link btn-sm p-0 text-muted" onClick={() => setBcRecipients((p) => p.map((r) => ({ ...r, checked: false })))}>keine</button>
                </div>
              </div>
              {bcRecipients.length === 0 ? (
                <div className="p-3 text-muted small">Keine Mitarbeiter mit Telefonnummer in dieser Gruppe.</div>
              ) : bcRecipients.map((r) => (
                <label key={r.id} className="d-flex align-items-center px-3 py-2 border-bottom" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox" className="form-check-input me-2" checked={r.checked}
                    onChange={() => setBcRecipients((p) => p.map((x) => (x.id === r.id ? { ...x, checked: !x.checked } : x)))}
                  />
                  <span className="small">{r.name} <span className="text-muted">· {r.phone}</span></span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WhatsAppInbox;
